import { motion } from 'motion/react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { colors, accent } from '@/lib/tokens';
import { Card } from '@/components/ui/card';

// ─── types ────────────────────────────────────────────────────────────────────

interface UsageData {
  projects:   { used: number; limit: number };
  apiKeys:    { used: number; limit: number };
  scansToday: { used: number; limit: number; resetsAt: string };
  endpoints:  { used: number; limit: number; projectName: string | null };
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function pct(used: number, limit: number) {
  return limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
}

function fillColor(p: number) {
  return p >= 75 ? accent : '#a8a29e';
}

// ─── MeterCell ────────────────────────────────────────────────────────────────

interface MeterCellProps {
  label: string;
  used: number;
  limit: number;
  sub?: string | null;
  borderRight?: boolean;
}

function MeterCell({ label, used, limit, sub, borderRight }: MeterCellProps) {
  const p = pct(used, limit);
  const fill = fillColor(p);
  const atLimit = p >= 95;

  return (
    <div
      className="px-5 py-5 sm:px-6"
      style={{
        flex: '1 1 0',
        minWidth: 0,
        borderRight: borderRight ? `1px solid ${colors.borderSubtle}` : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {/* Label */}
      <p
        className="text-xs uppercase tracking-[0.1em]"
        style={{ color: colors.textMuted, fontWeight: 600 }}
      >
        {label}
      </p>

      {/* Count */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span
          style={{
            fontFamily: "'Geist Mono', monospace",
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: colors.textPrimary,
            lineHeight: 1,
          }}
        >
          {used}
        </span>
        <span
          style={{
            fontFamily: "'Geist Mono', monospace",
            fontSize: 12,
            color: colors.textMuted,
            lineHeight: 1,
          }}
        >
          / {limit}
        </span>
        {atLimit && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: accent,
              marginLeft: 2,
            }}
          >
            {p >= 100 ? 'Limit reached' : 'Near limit'}
          </span>
        )}
      </div>

      {/* Bar */}
      <div
        style={{
          height: 3,
          borderRadius: 2,
          background: colors.bgSubtle,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <motion.div
          initial={false}
          animate={{ scaleX: p / 100 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: fill,
            borderRadius: 2,
            transformOrigin: 'left',
          }}
        />
      </div>

      {/* Sub-label */}
      {sub && (
        <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

// ─── PlanUsageSection ─────────────────────────────────────────────────────────

export function PlanUsageSection() {
  const { data, isLoading, isError } = useQuery<{ data: UsageData }>({
    queryKey: ['auth-usage'],
    queryFn: () => apiClient.get<{ data: UsageData }>('/auth/usage'),
    staleTime: 60_000,
  });

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 sm:px-6"
        style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
      >
        <p
          className="text-xs uppercase tracking-[0.1em]"
          style={{ color: colors.textMuted, fontWeight: 600 }}
        >
          Plan usage
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1 text-xs transition-colors"
          style={{ color: colors.textMuted, textDecoration: 'none' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
        >
          View plan details <ArrowRight size={12} />
        </Link>
      </div>

      {/* Meters */}
      {isLoading ? (
        <div className="flex plan-usage-grid">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="px-5 py-5 sm:px-6"
              style={{
                flex: '1 1 0',
                minWidth: 0,
                borderRight: i < 3 ? `1px solid ${colors.borderSubtle}` : undefined,
              }}
            >
              <div style={{ height: 10, width: '40%', borderRadius: 4, background: colors.bgSubtle, marginBottom: 12 }} />
              <div style={{ height: 20, width: '55%', borderRadius: 4, background: colors.bgSubtle, marginBottom: 10 }} />
              <div style={{ height: 3, width: '100%', borderRadius: 2, background: colors.bgSubtle }} />
            </div>
          ))}
        </div>
      ) : isError || !data ? null : (
        <div className="flex plan-usage-grid">
          <MeterCell
            label="Projects"
            used={data.data.projects.used}
            limit={data.data.projects.limit}
            borderRight
          />
          <MeterCell
            label="API keys"
            used={data.data.apiKeys.used}
            limit={data.data.apiKeys.limit}
            borderRight
          />
          <MeterCell
            label="Scans today"
            used={data.data.scansToday.used}
            limit={data.data.scansToday.limit}
            sub="Resets at midnight UTC"
            borderRight
          />
          <MeterCell
            label="Endpoints"
            used={data.data.endpoints.used}
            limit={data.data.endpoints.limit}
            sub={data.data.endpoints.projectName ?? undefined}
          />
        </div>
      )}
    </Card>
  );
}
